// All four states of the notes card are rendered server-side; this element only
// moves attributes between them. Nothing is created, fetched or re-rendered on
// click, so the illustration never reloads as the shopper moves between notes.
(() => {
  const HOTSPOT_SELECTOR = 'button[data-hira-note]';
  const DETAIL_SELECTOR = '[data-hira-note-detail]';
  const BACK_SELECTOR = '[data-hira-notes-back]';
  const ILLUSTRATION_SELECTOR = '.hira-hero__notes-illustration';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class HiraNotesProfile extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.hotspots = [...this.querySelectorAll(HOTSPOT_SELECTOR)];
      this.details = [...this.querySelectorAll(DETAIL_SELECTOR)];
      this.backButton = this.querySelector(BACK_SELECTOR);
      this.illustration = this.querySelector(ILLUSTRATION_SELECTOR);

      // One delegated listener rather than one per dot: the editor can add or
      // remove `note` blocks, and this way there is nothing to rebind.
      this.addEventListener('click', this.handleClick);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    get selected() {
      return this.dataset.hiraSelected ?? '';
    }

    handleClick(event) {
      const { target } = event;

      if (target.closest(BACK_SELECTOR)) {
        this.clear({ restoreFocus: true });
        return;
      }

      const hotspot = target.closest(HOTSPOT_SELECTOR);

      if (hotspot) this.select(hotspot.dataset.hiraNote);
    }


    /*
      The illustration moves between grid cells when a note opens, and that
      move is not transitionable: `grid-area` is a discrete property and the
      default and selected `grid-template-columns` have different track counts,
      so neither endpoint interpolates. This measures the jump the layout just
      made and hands the inverse back as a transform, which does animate --
      the grid still snaps, the artwork visually slides from where it was.
    */
    flip(mutate) {
      const target = this.illustration;

      if (!target || prefersReducedMotion() || typeof target.animate !== 'function') {
        mutate();
        return;
      }

      const first = target.getBoundingClientRect();

      // Clear any in-flight offset so `first` and `last` are both measured in
      // untransformed coordinates; otherwise a fast second click compounds.
      target.classList.remove('is-flipping');
      target.style.removeProperty('--hira-notes-flip-x');
      target.style.removeProperty('--hira-notes-flip-y');

      mutate();

      const last = target.getBoundingClientRect();
      const dx = first.left - last.left;
      const dy = first.top - last.top;

      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

      target.style.setProperty('--hira-notes-flip-x', `${dx.toFixed(2)}px`);
      target.style.setProperty('--hira-notes-flip-y', `${dy.toFixed(2)}px`);

      // Two frames: the first commits the inverted offset as the start value,
      // the second adds the transition and removes it so there is something to
      // animate towards. Committing both in one frame animates nothing.
      requestAnimationFrame(() => {
        target.classList.add('is-flipping');
        requestAnimationFrame(() => {
          target.style.removeProperty('--hira-notes-flip-x');
          target.style.removeProperty('--hira-notes-flip-y');
        });
      });
    }

    // Re-selecting the open note is a no-op, not a toggle back to the default
    // state — the design gives that job to the back arrow alone.
    select(tier) {
      if (!tier || tier === this.selected) return;

      const detail = this.details.find(({ dataset }) => dataset.hiraNoteDetail === tier);

      if (!detail) return;

      this.flip(() => {
        this.dataset.hiraSelected = tier;

        for (const hotspot of this.hotspots) {
          hotspot.setAttribute('aria-expanded', String(hotspot.dataset.hiraNote === tier));
        }

        for (const item of this.details) {
          item.hidden = item !== detail;
        }

        if (this.backButton) this.backButton.hidden = false;
      });
    }

    clear({ restoreFocus = false } = {}) {
      const previous = this.selected;

      this.flip(() => {
        delete this.dataset.hiraSelected;

        for (const hotspot of this.hotspots) {
          hotspot.setAttribute('aria-expanded', 'false');
        }

        for (const item of this.details) {
          item.hidden = true;
        }

        if (this.backButton) this.backButton.hidden = true;
      });

      // The back button is about to leave the accessibility tree, so focus
      // cannot stay on it — hand it to the dot the shopper came from.
      if (restoreFocus && previous) {
        this.hotspots.find(({ dataset }) => dataset.hiraNote === previous)?.focus();
      }
    }

    // Theme editor only: selecting a `note` block should show the state the
    // merchant is editing. `shopify_attributes` sits on the hotspot, so the
    // event target is the dot itself.
    handleBlockSelect({ target }) {
      if (!this.contains(target)) return;

      const hotspot = target.closest(HOTSPOT_SELECTOR);

      if (hotspot) this.select(hotspot.dataset.hiraNote);
    }

    // No focus move here — the merchant clicked in the editor sidebar, not on
    // the card, so moving focus would yank them out of the panel.
    handleBlockDeselect({ target }) {
      if (!this.contains(target)) return;

      this.clear();
    }
  }

  if (!customElements.get('hira-notes-profile')) {
    customElements.define('hira-notes-profile', HiraNotesProfile);
  }
})();
